import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { InputFieldType, SetupQuestion, slugifyKey } from '../servicesShared';
import { DirtySetter } from './editorShared';

export interface EditableSetupQuestion {
    key: string;
    question: string;
    type: InputFieldType;
    options: string; // comma-separated
    required: boolean;
}

export const setupToEditable = (setup?: SetupQuestion[] | null): EditableSetupQuestion[] =>
    (setup || []).map((item) => ({
        key: item.key,
        question: item.question,
        type: item.type || 'string',
        options: (item.options || []).join(', '),
        required: item.required !== false,
    }));

export const editableToSetup = (items: EditableSetupQuestion[]): SetupQuestion[] =>
    items
        .filter((item) => item.key.trim() && item.question.trim())
        .map((item) => {
            const options = item.options
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean);
            return {
                key: item.key.trim(),
                question: item.question.trim(),
                type: item.type,
                required: item.required,
                ...(options.length ? { options } : {}),
            };
        });

/** Questions the job settles with the customer on its first wake (replaces input_schema). */
export const SetupQuestionsEditor: React.FC<{
    items: EditableSetupQuestion[];
    onChange: DirtySetter<EditableSetupQuestion[]>;
}> = ({ items, onChange }) => {
    const update = (index: number, patch: Partial<EditableSetupQuestion>) =>
        onChange((prev) => prev.map((existing, i) => (i === index ? { ...existing, ...patch } : existing)));
    return (
        <div className="space-y-3">
            {items.map((item, index) => (
                <div key={index} className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        <Input
                            className="font-mono text-sm w-[200px]"
                            placeholder="question_key"
                            value={item.key}
                            onChange={(event) => update(index, { key: slugifyKey(event.target.value) })}
                        />
                        <Select value={item.type} onValueChange={(value) => update(index, { type: value as InputFieldType })}>
                            <SelectTrigger className="w-[130px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="string">Text</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="integer">Integer</SelectItem>
                                <SelectItem value="boolean">Yes / No</SelectItem>
                                <SelectItem value="array">Multi-select</SelectItem>
                            </SelectContent>
                        </Select>
                        <label className="flex items-center gap-1.5 text-sm whitespace-nowrap">
                            <Checkbox checked={item.required} onCheckedChange={(checked) => update(index, { required: checked === true })} />
                            Required
                        </label>
                        <div className="grow" />
                        <Button variant="ghost" size="icon" onClick={() => onChange((prev) => prev.filter((_, i) => i !== index))}>
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                    </div>
                    <Input
                        placeholder="The question, as the job will ask it"
                        value={item.question}
                        onChange={(event) => update(index, { question: event.target.value })}
                    />
                    {(item.type === 'string' || item.type === 'array') && (
                        <Input
                            placeholder={item.type === 'array' ? 'Choices, comma-separated' : 'Allowed answers, comma-separated (optional)'}
                            value={item.options}
                            onChange={(event) => update(index, { options: event.target.value })}
                        />
                    )}
                </div>
            ))}
            <Button
                variant="outline"
                size="sm"
                disabled={items.length >= 12}
                onClick={() => onChange((prev) => [...prev, { key: '', question: '', type: 'string', options: '', required: true }])}
            >
                <Plus className="h-4 w-4 mr-1" /> Add setup question
            </Button>
        </div>
    );
};

export default SetupQuestionsEditor;
